/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { StateAndCountrySelect, OpenSelect, Button, isLocationValid, EditInput } from '@piplapps/pipl-ui';
import styles from './OrganizationDetailsEdit.module.less';
import { getText } from '../../../Utils/TranslationUtils';
import { getError } from '../../../Utils/ErrorUtils';
import { getUseCaseTextFromKey, validateBasicEmail } from '../../../Utils/GenericUtils';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';

const organizationDetailsEdit = (props) => {
  const { organizationDetails, onOrganizationChange, onButtonClick, useCasesOptions, updateError, isLoading } = props;
  const [success, setSuccess] = useState(false);

  const validate = (values) => {
    const errors = {};
    for (const value in values) {
      if (value !== 'state' && value !== 'vat_id' && value !== 'subcategory' && value !== 'use_case') {
        if (values[value] === '') {
          errors[value] = gettext('Required');
        }
      }
    }

    if (!validateBasicEmail(values.invoice_email?.trim())) {
      errors.invoice_email = gettext('Invalid email address');
    }

    if (!values.subcategory && values.use_case !== 'PERSONAL' && organizationDetails.can_edit_use_case) {
      errors.subcategory = gettext('Required');
    }
    if (organizationDetails.can_edit_use_case && !values.use_case) {
      errors.use_case = gettext('Required');
    }

    if (!isLocationValid(values.country_code, values.state).state) {
      errors.state = gettext('Required');
    }

    if (!isLocationValid(values.country_code, values.state).country) {
      errors.country_code = gettext('Required');
    }

    return errors;
  };

  const formInitialValues = {
    name: organizationDetails?.name ? organizationDetails.name : '',
    phone: organizationDetails?.phone ? organizationDetails.phone : '',
    vat_id: organizationDetails?.vat_id ? organizationDetails.vat_id : '',
    address: organizationDetails?.address ? organizationDetails.address : '',
    invoice_email: organizationDetails?.invoice_email ? organizationDetails.invoice_email : '',
    city: organizationDetails?.city ? organizationDetails.city : '',
    postal_code: organizationDetails?.postal_code ? organizationDetails.postal_code : '',
    country_code: organizationDetails?.country_code ? organizationDetails.country_code : '',
    state: organizationDetails?.state ? organizationDetails.state : '',
    use_case: organizationDetails?.vetting_use_case_category_text
      ? organizationDetails?.vetting_use_case_category_text
      : '',
    subcategory: organizationDetails?.vetting_use_case_sub_category_text
      ? organizationDetails?.vetting_use_case_sub_category_text
      : '',
  };

  const formik = useFormik({
    initialValues: formInitialValues,
    validate,
    onSubmit: (values) => {
      const optionIndex = useCasesOptions.findIndex((i) => values.use_case === i.category.text_key);
      const subCategory = useCasesOptions[optionIndex]?.sub_categories?.find((i) => i.text_key === values.subcategory);

      onOrganizationChange({
        ...values,
        use_case_code: subCategory?.code || useCasesOptions[optionIndex]?.category?.code,
        vetting_use_case_sub_category_text_snapshot: getText(values.subcategory, 'en'),
        vetting_use_case_category_text_snapshot: getUseCaseTextFromKey(values.use_case),
      });
      if (success) {
        onButtonClick();
      }
    },
  });

  const getUseCasesOptions = () => {
    return useCasesOptions.map((o) => {
      return {
        label: getText(o.category.text_key),
        value: o.category.text_key,
      };
    });
  };

  const getSubcategoriesForUseCase = () => {
    if (formik.values.use_case) {
      const optionIndex = useCasesOptions.findIndex((i) => formik.values.use_case === i.category.text_key);
      const subcategories = useCasesOptions?.[optionIndex]?.sub_categories?.map((i) => i.text_key);
      if (subcategories) {
        return subcategories.map((subcategory) => {
          return {
            label: getText(subcategory),
            value: subcategory,
          };
        });
      }
    }

    return null;
  };

  const setCountryValue = (value) => {
    if (value !== 'US' || value !== 'CA') {
      formik.setFieldValue('state', null);
    }
    formik.setFieldValue('country_code', value);
  };
  const setStateValue = (value) => formik.setFieldValue('state', value);
  const setSubcategoryValue = (value) => formik.setFieldValue('subcategory', value);
  const setUseCaseValue = (value) => {
    setSubcategoryValue(null);
    formik.setFieldValue('use_case', value);
  };

  const useCaseOptions = getUseCasesOptions();
  const subcategoriesOptions = getSubcategoriesForUseCase();

  useEffect(() => {
    formik.setFieldValue('name', organizationDetails?.name || formik.values.name);
  }, [organizationDetails]);
  useEffect(() => {
    setSuccess(props.organizationDetailsUpdateStatus);
  }, [props.organizationDetailsUpdateStatus]);

  return (
    <div>
      <div>
        <div className={styles.topHeader} id="page-title">
          {gettext('Organization details')}
        </div>
        <div className={styles.header} id="page-title">
          {gettext('Details')}
        </div>
        <div>
          {/* <div className={styles.fieldsEdit} /> */}
          <form className={styles.formWrapper} onSubmit={formik.handleSubmit} id="organization-edit-form">
            <EditInput
              id="id_name"
              name="name"
              label={gettext('Organization name')}
              testId="name-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name ? formik.values.name : ''}
              error={formik.errors.name && formik.touched.name ? formik.errors.name : null}
            />
            <EditInput
              id="id_invoice_email"
              name="invoice_email"
              label={gettext('Invoice Email')}
              testId="email-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.invoice_email ? formik.values.invoice_email : ''}
              error={formik.errors.invoice_email && formik.touched.invoice_email ? formik.errors.invoice_email : null}
            />
            <EditInput
              id="id_phone"
              name="phone"
              label={gettext('Phone')}
              testId="phone-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phone ? formik.values.phone : ''}
              error={formik.errors.phone && formik.touched.phone ? formik.errors.phone : null}
            />
            <StateAndCountrySelect
              onBlur={formik.handleBlur}
              countryError={
                formik.errors.country_code && formik.touched.country_code ? formik.errors.country_code : null
              }
              stateError={formik.errors.state && formik.touched.state ? formik.errors.state : null}
              setCountryValue={setCountryValue}
              setStateValue={setStateValue}
              stateValue={formik.values.state}
              countryValue={formik.values.country_code}
            />
            <EditInput
              id="id_address"
              name="address"
              label={gettext('Address')}
              testId="address-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address ? formik.values.address : ''}
              error={formik.errors.address && formik.touched.address ? formik.errors.address : null}
            />
            <EditInput
              id="id_city"
              name="city"
              label={gettext('City')}
              testId="city-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.city ? formik.values.city : ''}
              error={formik.errors.city && formik.touched.city ? formik.errors.city : null}
            />
            <EditInput
              id="id_postal_code"
              name="postal_code"
              label={gettext('Postal Code')}
              testId="postal-code-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.postal_code ? formik.values.postal_code : ''}
              error={formik.errors.postal_code && formik.touched.postal_code ? formik.errors.postal_code : null}
            />
            <EditInput
              id="id_vat_id"
              name="vat_id"
              label={gettext('TAX ID')}
              testId="vat-input"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.vat_id ? formik.values.vat_id : ''}
              error={formik.errors.vat_id && formik.touched.vat_id ? formik.errors.vat_id : null}
            />
            {organizationDetails.can_edit_use_case ? (
              <>
                <OpenSelect
                  id="use-case-input"
                  name="use_case"
                  label={gettext('Category of Use')}
                  testId="use-case-input"
                  options={useCaseOptions}
                  onBlur={formik.handleBlur}
                  value={useCaseOptions.find((o) => o.value === formik.values.use_case)}
                  setValue={setUseCaseValue}
                  error={formik.errors.use_case && formik.touched.use_case ? formik.errors.use_case : null}
                />
                {formik.values.use_case === 'OTHER' ? (
                  <EditInput
                    id="subcategory-input"
                    name="subcategory"
                    label={gettext('Purpose of Use')}
                    testId="subcategory-input"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.subcategory ? formik.values.subcategory : ''}
                    error={formik.errors.subcategory && formik.touched.subcategory ? formik.errors.subcategory : null}
                  />
                ) : subcategoriesOptions?.length > 0 ? (
                  <OpenSelect
                    id="subcategory-input"
                    key={formik.values.subcategory}
                    name="subcategory"
                    label={gettext('Purpose of Use')}
                    testId="subcategory-input"
                    options={subcategoriesOptions}
                    onBlur={formik.handleBlur}
                    value={subcategoriesOptions.find((o) => o.value === formik.values.subcategory)}
                    setValue={setSubcategoryValue}
                    error={formik.errors.subcategory && formik.touched.subcategory ? formik.errors.subcategory : null}
                  />
                ) : null}
              </>
            ) : null}

            {success === false ? (
              <div className={styles.redText}>{getError(updateError?.address)}</div>
            ) : Object.keys(formik.errors).length > 0 && Object.keys(formik.touched).length > 8 ? (
              <div className={styles.redText}>{gettext('Your form has a problem.. please revise and try again')}</div>
            ) : null}
            <div className={styles.buttonsContainer}>
              <AnalyticsHandler
                eventName={AnalyticsEventNames.UPDATE_ORG_DETAILS}
                eventData={{ organizationDetails: formik.values, previousOrganizationDetails: formInitialValues }}
              >
                <Button
                  id="submit-button"
                  buttonClass="primary-blue"
                  type="submit"
                  text={gettext('Update')}
                  isLoading={isLoading}
                  loadingText={gettext('Updating')}
                />
              </AnalyticsHandler>
              <Button
                id="cancel-button"
                disabled={!organizationDetails.address}
                buttonClass="secondary-blue"
                text={gettext('Cancel')}
                margin={{ marginLeft: '15px' }}
                onClick={onButtonClick}
                type="button"
              />
            </div>
          </form>
        </div>
        <div className={styles.sectionSeparator} />
      </div>
    </div>
  );
};
organizationDetailsEdit.propTypes = {
  organizationDetails: PropTypes.object,
  useCasesOptions: PropTypes.array,
  onButtonClick: PropTypes.func,
  onOrganizationChange: PropTypes.func,
  organizationDetailsUpdateStatus: PropTypes.bool,
  updateError: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default organizationDetailsEdit;
